/** @jsx isolateComponent */
import { css } from '@emotion/react';
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import {
  AnchorTarget,
  Colors,
  Typography,
  Button,
} from '@zapier/design-system';
import { ZapCardSteps } from './ZapCardSteps';
import type { Step } from './ZapCardSteps';

type Props = {
  /**
   * The content to render for the details description. Typically this
   * uses the `ZapCardDetailsHtml` component.
   */
  children?: React.ReactNode;
  /**
   * The steps which comprise this Zap template.
   *
   * - `icon` is a URL representing an icon for the service used in this step.
   * - `id` is the ID of the service, e.g. "GoogleFormsAPI".
   * - `label` is the proper name of the trigger/action/search, e.g., "New Response in Spreadsheet".
   * - `name` is the name of the service, e.g., "Google Forms".
   */
  steps: Array<Step>;
  /**
   * `href` to a dedicated details page for the Zap template.
   */
  detailsHref?: string;
  /**
   * URL target. defaults to _self
   */
  detailsHrefTarget?: AnchorTarget;
  /**
   * `href` to use the Zap template.
   */
  editorHref?: string;
  /**
   * URL target. defaults to _self
   */
  editorHrefTarget?: AnchorTarget;
};

const Styles = labelStyles('ZapCardDetails', {
  root: css`
    display: grid;
    grid-template-columns: 100%;
    gap: 20px;
    box-sizing: content-box;
    padding: 0 35px 20px 35px;
    max-width: 770px;
    margin: 0 auto;
  `,
  description: css`
    ${Typography.paragraph3};
    color: ${Colors.gray80};
  `,
  actions: [
    css`
      display: flex;
      gap: 20px;
      max-width: 320px;
      margin-left: auto;
      justify-content: end;
    `,
  ],
});

/**
 * Details for a `ZapCard` component, like Zap steps,
 * a description of it, CTAs, etc.
 */
export const ZapCardDetails = (props: Props) => {
  const target = props.detailsHrefTarget || '_self';
  const editorTarget = props.editorHrefTarget || '_self';

  return (
    <div css={Styles.root}>
      <ZapCardSteps steps={props.steps} />
      {props.children && <div css={Styles.description}>{props.children}</div>}
      <div>
        <div css={Styles.actions}>
          {props.detailsHref && (
            <Button
              color="secondary"
              href={props.detailsHref}
              size="small"
              target={target}
            >
              See Zap details
            </Button>
          )}
          {props.editorHref && (
            <Button
              color="primary"
              hasShadow={true}
              href={props.editorHref}
              size="small"
              target={editorTarget}
            >
              Try it
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
